import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { studioApi } from '../lib/api';
import { toast } from 'sonner';
import { Stamp, Upload, Save, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const empty = {
  nome_completo: '', titolo: '', codice_fiscale: '', partita_iva: '',
  albo: '', numero_albo: '', provincia_albo: '',
  indirizzo_studio: '', comune_studio: '', cap_studio: '',
  telefono: '', email: '', pec: '',
  iban: '', cassa_previdenza: '',
  timbro_base64: '', firma_base64: '', note: ''
};

const fileToBase64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

export default function ProfessionistaPage() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const r = await studioApi.getProfessionista();
      if (r.data) setForm({ ...empty, ...r.data });
    } catch { toast.error('Errore caricamento profilo professionista'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.nome_completo) { toast.error('Nome completo obbligatorio'); return; }
    try {
      setSaving(true);
      await studioApi.upsertProfessionista(form);
      toast.success('Salvato');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const onFile = async (field, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Max 2MB'); return; }
    try {
      const b64 = await fileToBase64(file);
      setForm({ ...form, [field]: b64 });
      toast.success('Immagine caricata. Ricordati di salvare.');
    } catch { toast.error('Errore lettura file'); }
  };

  return (
    <div className="space-y-4 p-4 lg:p-6 max-w-4xl mx-auto" data-testid="professionista-page">
      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2"><Stamp className="w-6 h-6 text-indigo-600" /><h1 className="text-2xl font-bold">Profilo Professionista</h1></div>
          <p className="text-sm text-gray-600 mt-1">Dati del professionista titolare dello studio + timbro e firma da apporre sui documenti</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-professionista-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Salvataggio...' : 'Salva'}
        </Button>
      </motion.div>

      {loading ? <p className="text-gray-500 italic">Caricamento...</p> : (
        <div className="space-y-6">
          {/* Anagrafica */}
          <section className="bg-white border rounded-lg p-4">
            <h2 className="text-base font-semibold mb-3">Anagrafica</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><Label>Titolo</Label><Input placeholder="Arch., Ing., Geom., P.Ind." value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} data-testid="prof-titolo" /></div>
              <div className="md:col-span-2"><Label>Nome completo *</Label><Input value={form.nome_completo} onChange={(e) => setForm({ ...form, nome_completo: e.target.value })} data-testid="prof-nome" /></div>
              <div><Label>Codice Fiscale</Label><Input value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value })} /></div>
              <div><Label>Partita IVA</Label><Input value={form.partita_iva} onChange={(e) => setForm({ ...form, partita_iva: e.target.value })} /></div>
              <div><Label>Cassa previdenza</Label><Input placeholder="Inarcassa, EPAP..." value={form.cassa_previdenza} onChange={(e) => setForm({ ...form, cassa_previdenza: e.target.value })} /></div>
            </div>
          </section>

          {/* Iscrizione albo */}
          <section className="bg-white border rounded-lg p-4">
            <h2 className="text-base font-semibold mb-3">Iscrizione albo</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2"><Label>Ordine / Collegio</Label><Input placeholder="Ordine degli Architetti..." value={form.albo} onChange={(e) => setForm({ ...form, albo: e.target.value })} /></div>
              <div><Label>Numero albo</Label><Input value={form.numero_albo} onChange={(e) => setForm({ ...form, numero_albo: e.target.value })} /></div>
              <div><Label>Provincia albo</Label><Input value={form.provincia_albo} onChange={(e) => setForm({ ...form, provincia_albo: e.target.value })} maxLength={2} /></div>
            </div>
          </section>

          {/* Studio */}
          <section className="bg-white border rounded-lg p-4">
            <h2 className="text-base font-semibold mb-3">Recapiti studio</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2"><Label>Indirizzo studio</Label><Input value={form.indirizzo_studio} onChange={(e) => setForm({ ...form, indirizzo_studio: e.target.value })} /></div>
              <div><Label>CAP</Label><Input value={form.cap_studio} onChange={(e) => setForm({ ...form, cap_studio: e.target.value })} /></div>
              <div><Label>Comune</Label><Input value={form.comune_studio} onChange={(e) => setForm({ ...form, comune_studio: e.target.value })} /></div>
              <div><Label>Telefono</Label><Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>PEC</Label><Input value={form.pec} onChange={(e) => setForm({ ...form, pec: e.target.value })} /></div>
              <div><Label>IBAN</Label><Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} /></div>
            </div>
          </section>

          {/* Timbro & Firma */}
          <section className="bg-white border rounded-lg p-4">
            <h2 className="text-base font-semibold mb-3">Timbro & Firma</h2>
            <p className="text-xs text-gray-500 mb-3">PNG o JPG, max 2MB. Verranno utilizzati nei documenti generati (placeholder «Timbro_Professionista» e «Firma_Professionista»).</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { key: 'timbro_base64', label: 'Timbro professionale' },
                { key: 'firma_base64', label: 'Firma autografa' },
              ].map(({ key, label }) => (
                <div key={key} className="border-2 border-dashed border-gray-300 rounded-lg p-3">
                  <Label className="font-semibold">{label}</Label>
                  <div className="mt-2 flex items-center justify-center bg-gray-50 rounded h-40 overflow-hidden">
                    {form[key] ? (
                      <img src={form[key]} alt={label} className="max-h-full max-w-full object-contain" />
                    ) : (
                      <span className="text-xs text-gray-400 italic">Nessuna immagine caricata</span>
                    )}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <label className="flex-1 cursor-pointer">
                      <input type="file" accept="image/png,image/jpeg" className="hidden"
                        onChange={(e) => onFile(key, e.target.files?.[0])} />
                      <span className="inline-flex w-full items-center justify-center gap-1 px-3 py-1.5 rounded bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700">
                        <Upload className="w-3.5 h-3.5" /> Carica
                      </span>
                    </label>
                    {form[key] && (
                      <Button size="sm" variant="outline" onClick={() => setForm({ ...form, [key]: '' })}>
                        <Trash2 className="w-3.5 h-3.5 mr-1 text-red-500" /> Rimuovi
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-white border rounded-lg p-4">
            <Label>Note</Label>
            <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </section>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="save-professionista-bottom-btn">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Salvataggio...' : 'Salva profilo'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
